/**
 * useSubscription Hook
 * Fetch current subscription plan data using React Query
 *
 * Use this hook when you need:
 * - Plan tier and status
 * - Billing period and next charge date
 * - Plan limits (SKU, SmartImport)
 * - Pending changes (scheduled downgrades)
 *
 * Stale time: 5 minutes (plan data changes infrequently)
 *
 * NOTE: Backend returns org-centric subscription data (orgId, planName, status,
 * skuLimit, etc.). This hook transforms it into the full CurrentPlan format
 * expected by UI. When backend implements full subscription tracking, this will
 * seamlessly work.
 */

import { useQuery } from '@tanstack/react-query'
import { billingService } from '../services/billingService'
import type { CurrentPlan, PlanTier, SubscriptionStatus, PaymentStatus } from '../types/billing.types'

/**
 * Backend response format from /billing/current-plan
 */
interface BackendPlanResponse {
  uid?: string
  orgId?: string
  userId?: string
  planTierId?: string
  planName?: string
  status?: string
  skuLimit?: number
  smartimportLimit?: number
  currentPeriodEnd?: string | number | null
  paymentStatus?: string
  trialEnd?: string | number | null
  billingPeriod?: string | null
  // Full format fields (when backend fully implements)
  tier?: PlanTier
  pendingChange?: { newTier: string; effectiveDate: number }
}

/**
 * Valid plan tiers (must match PLAN_TIERS keys in billing.types.ts)
 */
const VALID_TIERS: PlanTier[] = ['free', 'growth', 'professional', 'business']

/**
 * Check if a tier value is valid
 */
function isValidTier(tier?: string): tier is PlanTier {
  return !!tier && VALID_TIERS.includes(tier as PlanTier)
}

/**
 * Map plan name to tier enum
 */
function planNameToTier(planName?: string): PlanTier {
  if (!planName) return 'free'
  const normalized = planName.toLowerCase()
  if (normalized.includes('business')) return 'business'
  if (normalized.includes('professional')) return 'professional'
  if (normalized.includes('growth')) return 'growth'
  return 'free'
}

/**
 * Validate and normalize subscription status
 */
function normalizeStatus(status?: string): SubscriptionStatus {
  const validStatuses: SubscriptionStatus[] = ['active', 'trialing', 'past_due', 'canceled', 'incomplete', 'unpaid']
  if (status && validStatuses.includes(status as SubscriptionStatus)) {
    return status as SubscriptionStatus
  }
  return 'active' // Default to active if unknown
}

/**
 * Validate and normalize payment status
 */
function normalizePaymentStatus(status?: string): PaymentStatus {
  const validStatuses: PaymentStatus[] = ['succeeded', 'failed', 'pending', 'n/a']
  if (status && validStatuses.includes(status as PaymentStatus)) {
    return status as PaymentStatus
  }
  return 'n/a'
}

/**
 * Parse timestamp to Unix seconds (handles ISO string, ms, or seconds)
 */
function parseTimestamp(value: string | number | null | undefined): number | null {
  if (!value) return null
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return isNaN(ms) ? null : Math.floor(ms / 1000)
  }
  // If already number, check if ms or seconds
  return value > 1e12 ? Math.floor(value / 1000) : value
}

/**
 * Transform backend response to CurrentPlan format
 */
function transformPlanResponse(data: BackendPlanResponse): CurrentPlan {
  // Validate tier - if invalid or undefined, derive from planName or default to 'free'
  const tier = isValidTier(data.tier) ? data.tier : planNameToTier(data.planName)

  return {
    tier,
    status: normalizeStatus(data.status),
    billingPeriod: (data.billingPeriod as 'monthly' | 'annual' | null) ?? null,
    skuLimit: data.skuLimit ?? 1000,
    smartimportLimit: data.smartimportLimit ?? -1, // -1 means unlimited
    currentPeriodEnd: parseTimestamp(data.currentPeriodEnd),
    paymentStatus: normalizePaymentStatus(data.paymentStatus),
    trialEnd: parseTimestamp(data.trialEnd),
    pendingChange: data.pendingChange
  }
}

export function useSubscription(options?: { enabled?: boolean }) {
  return useQuery<CurrentPlan>({
    queryKey: ['subscription', 'current-plan'],
    queryFn: async () => {
      const response = await billingService.getCurrentPlan()

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch subscription')
      }

      // Transform backend response to full CurrentPlan format
      return transformPlanResponse(response.data as unknown as BackendPlanResponse)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    enabled: options?.enabled ?? true
  })
}
